import { useEffect, useState } from "react";
import clsx from "clsx";
import { Environment } from "@environments/environment";
import { useT } from "@app/contexts/preferences.context";
import { IcX } from "../icons";

declare global {
  interface Window {
    subiz?: (command: string, ...args: unknown[]) => void;
    /** store nội bộ của Subiz, dùng để biết khi khung chat bị đóng */
    sbz_store?: { onWidgetClose: (ctx: { _isMounted: boolean }, cb: () => void) => () => void };
  }
}

/** Biểu tượng Messenger (lấy từ FacebookButton cũ) */
const MessengerIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className={className}>
    <defs>
      <linearGradient id="support-chat-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00C6FF" />
        <stop offset="60%" stopColor="#0078FF" />
        <stop offset="100%" stopColor="#B210FF" />
      </linearGradient>
    </defs>
    <path
      fill="url(#support-chat-gradient)"
      d="M256 32C132.3 32 32 124.9 32 239.4c0 65.1 32.7 123.3 83.8 161.2V480l75.4-41.5c20.8 5.8 42.9 8.9 64.8 8.9 123.7 0 224-92.9 224-207.4S379.7 32 256 32z"
    />
    <path
      fill="#FFFFFF"
      d="M118 280.9l75.8-120.7c11.9-19 38.2-22.9 55.1-8.2l59.6 51.8c5.4 4.7 13.1 4.7 18.5 0l81.5-62c11-8.4 25.8 4.5 18.7 15.9l-75.8 120.7c-11.9 19-38.2 22.9-55.1 8.2l-59.6-51.8c-5.4-4.7-13.1-4.7-18.5 0l-81.5 62c-11 8.4-25.8-4.5-18.7-15.9z"
    />
  </svg>
);

/** Biểu tượng Discord (lấy từ DiscordButton cũ) */
const DiscordIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

/**
 * Nút hỗ trợ gộp: 1 nút Messenger ở góc phải, bấm để mở/thu gọn
 * Messenger (Fanpage), Discord và Chat trực tuyến (Subiz).
 * Nút tròn mặc định của Subiz được ẩn bằng CSS (.widget-layout .chat-button trong app.scss),
 * khung chat Subiz chỉ mở khi bấm "Chat trực tuyến".
 */
const OPEN_SUPPORT_EVENT = "pcr:open-support";

/** Mở danh sách kênh hỗ trợ (Messenger, Discord, Chat trực tuyến) ở góc màn hình. */
export const openSupport = () => window.dispatchEvent(new Event(OPEN_SUPPORT_EVENT));

function SupportChat() {
  const t = useT();
  const [open, setOpen] = useState(false);
  // Đang mở khung chat Subiz: ẩn nút để không che ô nhập tin nhắn
  const [chatting, setChatting] = useState(false);

  // Các trang khác mở khung hỗ trợ bằng openSupport() (ví dụ "Liên hệ hỗ trợ" ở trang Tài khoản)
  useEffect(() => {
    const onOpen = () => setOpen(true);

    window.addEventListener(OPEN_SUPPORT_EVENT, onOpen);

    return () => window.removeEventListener(OPEN_SUPPORT_EVENT, onOpen);
  }, []);

  const openSubizChat = () => {
    window.subiz?.("showWidget");
    setOpen(false);

    const store = window.sbz_store;

    if (store) {
      setChatting(true);

      const unsubscribe = store.onWidgetClose({ _isMounted: true }, () => {
        setChatting(false);
        unsubscribe?.();
      });
    }
  };

  if (chatting) return null;

  const item =
    "flex items-center gap-3 rounded-full border border-line bg-surface py-1.5 pl-1.5 pr-4 text-[13px] font-semibold text-fg shadow-card transition-transform hover:-translate-y-0.5";
  const iconWrap = "grid h-10 w-10 flex-none place-items-center rounded-full";

  return (
    <div className="fixed bottom-5 right-5 z-[99999] flex flex-col items-end gap-2.5">
      {open && (
        <>
          <a href={Environment.FACEBOOK_PAGE} target="_blank" rel="noopener noreferrer" className={item}>
            <span className={clsx(iconWrap, "bg-surface-2")}>
              <MessengerIcon className="h-6 w-6" />
            </span>
            {t("Messenger Fanpage")}
          </a>
          <a href={Environment.DISCORD_PAGE} target="_blank" rel="noopener noreferrer" className={item}>
            <span className={clsx(iconWrap, "bg-surface-2")}>
              <DiscordIcon className="h-6 w-6 fill-[#5865F2]" />
            </span>
            Discord
          </a>
          <button
            type="button"
            className={item}
            onClick={openSubizChat}
          >
            <span className={clsx(iconWrap, "bg-brand text-white")}>
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.5A8 8 0 1 1 21 12z" strokeLinejoin="round" />
              </svg>
            </span>
            {t("Chat trực tuyến")}
          </button>
        </>
      )}

      <button
        type="button"
        aria-expanded={open}
        aria-label={open ? t("Thu gọn hỗ trợ") : t("Mở hỗ trợ")}
        title={open ? t("Thu gọn hỗ trợ") : t("Mở hỗ trợ")}
        onClick={() => setOpen(!open)}
        className="grid h-14 w-14 place-items-center rounded-full border border-line bg-surface shadow-lg shadow-black/25 transition-transform hover:scale-105 active:scale-95"
      >
        {open ? <IcX className="h-6 w-6 text-fg" /> : <MessengerIcon className="h-8 w-8" />}
      </button>
    </div>
  );
}

export default SupportChat;
